import { create } from 'zustand';

/**
 * @typedef {Object} Notification
 * @property {string} id
 * @property {string} title
 * @property {string} message
 * @property {'info'|'success'|'warning'|'error'} type
 * @property {boolean} isRead
 * @property {string} createdAt
 */

/** @typedef {'light'|'dark'|'system'} ThemePreference */

const THEME_STORAGE_KEY = 'theme';

/** Reads the stored preference, falling back to 'system' when absent or unreadable. */
function readStoredTheme() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  } catch {
    return 'system';
  }
}

/**
 * True when the OS is currently asking for a dark UI. Deliberately does NOT
 * swallow: callers decide whether a broken `matchMedia` is fatal, so the store
 * can log it and leave state untouched rather than silently reporting light.
 */
function prefersDark() {
  return globalThis.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Resolves a preference to the mode actually rendered. */
function resolveTheme(preference) {
  return preference === 'system' ? prefersDark() : preference === 'dark';
}

/** Seeding the store must never throw at import time, so this variant is safe. */
function resolveThemeSafe(preference) {
  try {
    return resolveTheme(preference);
  } catch {
    return false;
  }
}

/**
 * Puts the resolved mode on <html>. Every themed colour in the app is a CSS
 * variable keyed off this one class, so this is the only place the DOM is
 * touched for theming — portalled UI included, since the variables inherit
 * from the root element.
 */
function applyTheme(isDark) {
  try {
    document.documentElement.classList.toggle('dark', isDark);
  } catch {
    /* no document (SSR, non-DOM test env) — nothing to paint */
  }
}

/**
 * @typedef {Object} UIStore
 * @property {ThemePreference} theme
 * @property {boolean} isDarkMode
 * @property {boolean} isSidebarOpen
 * @property {Notification[]} notifications
 * @property {() => void} initializeTheme
 * @property {(preference: ThemePreference) => void} setTheme
 * @property {() => void} toggleTheme
 * @property {() => void} toggleSidebar
 * @property {(open: boolean) => void} setSidebarOpen
 * @property {(notification: Omit<Notification, 'id' | 'isRead' | 'createdAt'>) => void} addNotification
 * @property {(id: string) => void} markAsRead
 * @property {() => void} markAllAsRead
 * @property {(id: string) => void} removeNotification
 */

export const useUIStore = create((set, get) => ({
  // Seeded from storage so the first render already agrees with the class the
  // inline script in index.html put on <html> — otherwise the app paints light
  // for a frame before correcting itself.
  theme: readStoredTheme(),
  isDarkMode: resolveThemeSafe(readStoredTheme()),
  isSidebarOpen: false,
  notifications: [],

  /**
   * Applies the stored preference and, while the user is on 'system', keeps the
   * app in step with the OS switching theme under a running tab.
   */
  initializeTheme: () => {
    try {
      const preference = readStoredTheme();
      const isDark = resolveTheme(preference);
      applyTheme(isDark);
      set({ theme: preference, isDarkMode: isDark });

      const query = globalThis.matchMedia('(prefers-color-scheme: dark)');
      const onSystemChange = (event) => {
        // An explicit light/dark choice wins over the OS until it is cleared.
        if (get().theme !== 'system') return;
        applyTheme(event.matches);
        set({ isDarkMode: event.matches });
      };
      query.addEventListener('change', onSystemChange);
      return () => query.removeEventListener('change', onSystemChange);
    } catch (error) {
      console.error('Failed to initialize theme:', error);
      return undefined;
    }
  },

  setTheme: (preference) => {
    try {
      const isDark = resolveTheme(preference);
      applyTheme(isDark);
      // Persist before committing to state: if storage rejects the write the
      // store stays as it was, rather than reporting a preference that would
      // not survive a reload.
      localStorage.setItem(THEME_STORAGE_KEY, preference);
      set({ theme: preference, isDarkMode: isDark });
    } catch (error) {
      console.error('Failed to toggle theme:', error);
    }
  },

  /** Flips to the opposite of what is on screen, pinning the choice explicitly. */
  toggleTheme: () => get().setTheme(get().isDarkMode ? 'light' : 'dark'),

  toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),

  setSidebarOpen: (open) => set({ isSidebarOpen: open }),

  addNotification: (notification) =>
    set((state) => ({
      notifications: [
        {
          id: crypto.randomUUID(),
          isRead: false,
          createdAt: new Date().toISOString(),
          ...notification,
        },
        ...state.notifications,
      ],
    })),

  markAsRead: (id) =>
    set((state) => ({
      notifications: state.notifications.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
    })),

  markAllAsRead: () =>
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
    })),

  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),
}));
